import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/common/Header';
import api from '../services/api';
import {
  getActivities,
  formatTimeAgo,
  type Activity as ActivityType,
} from '../utils/activityLogger';
import {
  Cloud,
  TrendingUp,
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  Zap,
  Activity,
  MapPin,
  Droplets,
  Shield,
  Clock,
  Info,
  Target,
  DollarSign,
  HelpCircle,
  ArrowRight,
  X,
} from 'lucide-react';

// South African cities with coordinates
const CITIES = [
  { name: 'Johannesburg', lat: -26.2041, lon: 28.0473 },
  { name: 'Cape Town', lat: -33.9249, lon: 18.4241 },
  { name: 'Durban', lat: -29.8587, lon: 31.0218 },
  { name: 'Pretoria', lat: -25.7479, lon: 28.2293 },
  { name: 'Port Elizabeth', lat: -33.9608, lon: 25.6022 },
  { name: 'Bloemfontein', lat: -29.0852, lon: 26.1596 },
];

export default function Dashboard() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    activeRules: 0,
    todayAdjustments: 0,
    budgetSaved: 0,
  });
  const [satelliteImageKey, setSatelliteImageKey] = useState(Date.now());
  const [activities, setActivities] = useState<ActivityType[]>([]);
  const [selectedCity, setSelectedCity] = useState(() => {
    // Load saved city from localStorage, default to Pretoria
    return localStorage.getItem('selectedCity') || 'Pretoria';
  });
  const [weather, setWeather] = useState({
    openMeteo: {
      temperature: 0,
      rainProbability: 0,
      humidity: 0,
      windSpeed: 0,
    },
    weatherApi: {
      temperature: 0,
      rainProbability: 0,
      humidity: 0,
      windSpeed: 0,
    },
    loading: true,
  });
  const [lastExecution, setLastExecution] = useState<any>(null);
  const [loadingExecution, setLoadingExecution] = useState(true);
  // Initialize with safe default (true)
  const [dryRunMode, setDryRunMode] = useState(true);

  // Help modals state
  const [showWeatherSourceHelp, setShowWeatherSourceHelp] = useState(false);
  const [showLiveModeHelp, setShowLiveModeHelp] = useState(false);
  const [showNextStepsModal, setShowNextStepsModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Load settings from API on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await api.get('/settings');
        if (response.data.success && response.data.settings) {
          setDryRunMode(response.data.settings.dry_run_mode ?? true);
        }
      } catch (error) {
        console.error('Failed to load settings from API, using default:', error);
        // Fallback to localStorage
        const settings = localStorage.getItem('weatherAdsSettings');
        if (settings) {
          const parsed = JSON.parse(settings);
          setDryRunMode(parsed.dryRunMode ?? true);
        }
      }
    };

    if (isAuthenticated) {
      loadSettings();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch campaigns (flatten from all accounts)
        const campaignsResponse = await api.get('/campaigns');
        let totalCampaigns = 0;
        if (campaignsResponse.data.success && campaignsResponse.data.accounts) {
          campaignsResponse.data.accounts.forEach((account: any) => {
            if (account.campaigns && account.campaigns.length > 0) {
              totalCampaigns += account.campaigns.length;
            }
          });
        }

        // Fetch rules
        const rulesResponse = await api.get('/rules');
        const allRules = rulesResponse.data.rules || [];
        const activeRules = allRules.filter((rule: any) => rule.is_active).length;

        // Fetch today's executions
        const today = new Date().toISOString().split('T')[0];
        const executionsResponse = await api.get('/executions?limit=1000');
        const todayExecutions = executionsResponse.data.executions?.filter((exec: any) => {
          const execDate = new Date(exec.executed_at).toISOString().split('T')[0];
          return execDate === today && exec.success;
        }) || [];
        const todayAdjustments = todayExecutions.length;

        // Calculate budget saved from today's successful executions
        const budgetSaved = todayExecutions.reduce((total: number, exec: any) => {
          if (exec.old_budget_micros && exec.new_budget_micros) {
            const oldBudget = exec.old_budget_micros / 1_000_000; // Convert micros to Rand
            const newBudget = exec.new_budget_micros / 1_000_000;
            const difference = oldBudget - newBudget;
            // Positive difference = budget decreased (saved)
            // Negative difference = budget increased (spent more)
            return total + difference;
          }
          return total;
        }, 0);

        setStats({
          totalCampaigns,
          activeRules,
          todayAdjustments,
          budgetSaved,
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
        // Keep default values on error
        setStats({
          totalCampaigns: 0,
          activeRules: 0,
          todayAdjustments: 0,
          budgetSaved: 0,
        });
      }
    };

    if (isAuthenticated) {
      fetchStats();
    }
  }, [isAuthenticated]);

  // Fetch last execution for automation status
  useEffect(() => {
    const fetchLastExecution = async () => {
      try {
        setLoadingExecution(true);
        const response = await api.get('/executions?limit=1');

        if (response.data.success && response.data.executions && response.data.executions.length > 0) {
          setLastExecution(response.data.executions[0]);
        } else {
          setLastExecution(null);
        }
      } catch (error) {
        console.error('Failed to fetch last execution:', error);
        setLastExecution(null);
      } finally {
        setLoadingExecution(false);
      }
    };

    if (isAuthenticated) {
      fetchLastExecution();
    }
  }, [isAuthenticated]);

  // Auto-refresh satellite image every 30 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      setSatelliteImageKey(Date.now());
    }, 30 * 60 * 1000); // 30 minutes

    return () => clearInterval(interval);
  }, []);

  // Load activities on mount and listen for updates
  useEffect(() => {
    // Load initial activities
    setActivities(getActivities());

    // Listen for activity updates from other components
    const handleActivitiesUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<ActivityType[]>;
      setActivities(customEvent.detail);
    };

    window.addEventListener('activitiesUpdated', handleActivitiesUpdate);

    return () => {
      window.removeEventListener('activitiesUpdated', handleActivitiesUpdate);
    };
  }, []);

  // Listen for dry run mode changes from Settings
  useEffect(() => {
    // Handle cross-tab changes (storage event)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'weatherAdsSettings' && e.newValue) {
        const settings = JSON.parse(e.newValue);
        setDryRunMode(settings.dryRunMode ?? true);
      }
    };

    // Handle same-tab changes (custom event from Settings)
    const handleSettingsChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.dryRunMode !== undefined) {
        setDryRunMode(customEvent.detail.dryRunMode);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('settingsChanged', handleSettingsChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('settingsChanged', handleSettingsChange);
    };
  }, []);

  // Fetch live weather data for selected city from BOTH sources
  useEffect(() => {
    const fetchWeather = async () => {
      const city = CITIES.find(c => c.name === selectedCity);
      if (!city) return;

      try {
        setWeather(prev => ({ ...prev, loading: true }));

        // Fetch from Open-Meteo (Source 1)
        const openMeteoPromise = fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${city.lat}&longitude=${city.lon}&current=temperature_2m,precipitation_probability,relative_humidity_2m,wind_speed_10m&timezone=Africa/Johannesburg`
        ).then(res => res.json());

        // Fetch from WeatherAPI.com (Source 2)
        const weatherApiKey = '92bcc84f1b7d45df865143932241605';
        const weatherApiPromise = fetch(
          `https://api.weatherapi.com/v1/current.json?key=${weatherApiKey}&q=${city.lat},${city.lon}&aqi=no`
        ).then(res => res.json()).catch(() => null); // Graceful fallback if API key not set

        const [openMeteoData, weatherApiData] = await Promise.all([openMeteoPromise, weatherApiPromise]);

        setWeather({
          openMeteo: {
            temperature: Math.round(openMeteoData.current.temperature_2m),
            rainProbability: openMeteoData.current.precipitation_probability || 0,
            humidity: Math.round(openMeteoData.current.relative_humidity_2m) || 0,
            windSpeed: Math.round(openMeteoData.current.wind_speed_10m) || 0,
          },
          weatherApi: weatherApiData ? {
            temperature: Math.round(weatherApiData.current.temp_c),
            rainProbability: weatherApiData.current.precip_mm > 0
              ? Math.min(100, Math.round(weatherApiData.current.precip_mm * 20)) // Estimate from precipitation
              : (weatherApiData.current.cloud || 0) / 2, // Fallback: use cloud cover as proxy
            humidity: Math.round(weatherApiData.current.humidity) || 0,
            windSpeed: Math.round(weatherApiData.current.wind_kph) || 0,
          } : {
            temperature: 0,
            rainProbability: 0,
            humidity: 0,
            windSpeed: 0,
          },
          loading: false,
        });
      } catch (error) {
        console.error('Failed to fetch weather:', error);
        setWeather(prev => ({ ...prev, loading: false }));
      }
    };

    fetchWeather();

    // Refresh weather every 15 minutes
    const interval = setInterval(fetchWeather, 15 * 60 * 1000);

    return () => clearInterval(interval);
  }, [selectedCity]);

  // Save selected city to localStorage when it changes
  const handleCityChange = (cityName: string) => {
    setSelectedCity(cityName);
    localStorage.setItem('selectedCity', cityName);
  };

  if (!isAuthenticated) {
    return null;
  }

  // Helper function to get icon component based on activity type
  const getActivityIcon = (icon: ActivityType['icon']) => {
    switch (icon) {
      case 'plus':
        return <Plus className="h-5 w-5" />;
      case 'edit':
        return <Edit2 className="h-5 w-5" />;
      case 'trash':
        return <Trash2 className="h-5 w-5" />;
      case 'toggle':
        return <ToggleLeft className="h-5 w-5" />;
      default:
        return <Activity className="h-5 w-5" />;
    }
  };

  // Helper function to get background color based on activity color
  const getActivityColorClasses = (color: ActivityType['color']) => {
    switch (color) {
      case 'emerald':
        return 'bg-emerald-900/30 border-emerald-700 text-emerald-400';
      case 'cyan':
        return 'bg-cyan-900/30 border-cyan-700 text-cyan-400';
      case 'red':
        return 'bg-red-900/30 border-red-700 text-red-400';
      case 'amber':
        return 'bg-amber-900/30 border-amber-700 text-amber-400';
      default:
        return 'bg-gray-900/30 border-gray-700 text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Banner */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-6 mb-8 text-white">
          <h2 className="text-2xl font-bold mb-2">Welcome to Weather Ads Automation!</h2>
          <p className="text-gray-300">
            Your campaigns are being optimized based on real-time weather conditions.
          </p>
        </div>

        {/* 🛠️ TEMPORARY DEV MODAL - Remove before production */}
        <div className="mb-8 relative">
          <button
            onClick={() => setShowNextStepsModal(true)}
            className="w-full bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-500 hover:from-blue-500 hover:via-cyan-500 hover:to-teal-400 border-2 border-cyan-400 rounded-xl shadow-xl p-5 transition-all transform hover:scale-[1.01]"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white rounded-full p-2">
                  <Info className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
                    🛠️ Development Status Update
                    <span className="text-xs font-normal bg-amber-500/20 border border-amber-400 rounded px-2 py-0.5">TEMP - DEV ONLY</span>
                  </h3>
                  <p className="text-white text-sm">
                    ✅ Peppermills account fixed! All 8 accounts now properly linked. Click to view details.
                  </p>
                </div>
              </div>
              <ArrowRight className="w-6 h-6 text-white flex-shrink-0" />
            </div>
          </button>
        </div>

        {/* Stats Grid - Clickable Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Campaigns */}
          <button
            onClick={() => navigate('/campaigns')}
            className="group bg-gray-800 border-2 border-gray-700 rounded-lg shadow-lg p-6 hover:border-cyan-500 hover:scale-105 transition-all duration-200 cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-cyan-900/30 border border-cyan-700 rounded-lg group-hover:bg-cyan-900/50 group-hover:border-cyan-500 transition-all">
                <Target className="h-6 w-6 text-cyan-400" />
              </div>
            </div>
            <h3 className="text-gray-400 text-sm font-medium group-hover:text-cyan-300 transition-colors">
              Total Campaigns
            </h3>
            <p className="text-3xl font-bold text-white mt-2">
              {stats.totalCampaigns}
            </p>
            <div className="flex items-center gap-1 text-xs text-cyan-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <span>View campaigns</span>
              <ArrowRight className="w-3 h-3" />
            </div>
          </button>

          {/* Active Rules */}
          <button
            onClick={() => navigate('/rules')}
            className="group bg-gray-800 border-2 border-gray-700 rounded-lg shadow-lg p-6 hover:border-emerald-500 hover:scale-105 transition-all duration-200 cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-emerald-900/30 border border-emerald-700 rounded-lg group-hover:bg-emerald-900/50 group-hover:border-emerald-500 transition-all">
                <Zap className="h-6 w-6 text-emerald-400" />
              </div>
            </div>
            <h3 className="text-gray-400 text-sm font-medium group-hover:text-emerald-300 transition-colors">
              Active Rules
            </h3>
            <p className="text-3xl font-bold text-white mt-2">
              {stats.activeRules}
            </p>
            <div className="flex items-center gap-1 text-xs text-emerald-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <span>Manage rules</span>
              <ArrowRight className="w-3 h-3" />
            </div>
          </button>

          {/* Today's Adjustments */}
          <button
            onClick={() => navigate('/history')}
            className="group bg-gray-800 border-2 border-gray-700 rounded-lg shadow-lg p-6 hover:border-amber-500 hover:scale-105 transition-all duration-200 cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-amber-900/30 border border-amber-700 rounded-lg group-hover:bg-amber-900/50 group-hover:border-amber-500 transition-all">
                <TrendingUp className="h-6 w-6 text-amber-400" />
              </div>
              <div className="relative group/info">
                <Info className="h-4 w-4 text-gray-500 hover:text-amber-400 transition-colors cursor-help" />
                <div className="absolute right-0 top-6 w-72 bg-gray-900 border border-amber-700 rounded-lg p-3 shadow-xl opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-200 z-10 pointer-events-none">
                  <p className="text-xs text-gray-300 mb-2">
                    <span className="font-semibold text-amber-400">What this counts:</span>
                  </p>
                  <p className="text-xs text-gray-400 mb-2">
                    Actual bid/budget changes <strong>executed</strong> by the automation when weather conditions match your rules.
                  </p>
                  <p className="text-xs text-gray-400 mb-2">
                    <span className="text-amber-400">Does NOT count:</span> Rule edits, rule activations, or automation checks that didn't trigger changes.
                  </p>
                  <p className="text-xs text-gray-500 italic">
                    Tip: Lower rule thresholds to test (e.g., rain ≥0% instead of ≥50%)
                  </p>
                </div>
              </div>
            </div>
            <h3 className="text-gray-400 text-sm font-medium group-hover:text-amber-300 transition-colors">
              Today's Adjustments
            </h3>
            <p className="text-3xl font-bold text-white mt-2">
              {stats.todayAdjustments}
            </p>
            <div className="flex items-center gap-1 text-xs text-amber-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <span>View history</span>
              <ArrowRight className="w-3 h-3" />
            </div>
          </button>

          {/* Budget Saved */}
          <button
            onClick={() => navigate('/history')}
            className="group bg-gray-800 border-2 border-gray-700 rounded-lg shadow-lg p-6 hover:border-blue-500 hover:scale-105 transition-all duration-200 cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-900/30 border border-blue-700 rounded-lg group-hover:bg-blue-900/50 group-hover:border-blue-500 transition-all">
                <DollarSign className="h-6 w-6 text-blue-400" />
              </div>
              <div className="relative group/info">
                <Info className="h-4 w-4 text-gray-500 hover:text-blue-400 transition-colors cursor-help" />
                <div className="absolute right-0 top-6 w-72 bg-gray-900 border border-blue-700 rounded-lg p-3 shadow-xl opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-200 z-10 pointer-events-none">
                  <p className="text-xs text-gray-300 mb-2">
                    <span className="font-semibold text-blue-400">How this is calculated:</span>
                  </p>
                  <p className="text-xs text-gray-400 mb-2">
                    Sum of all <strong>daily budget reductions</strong> from successful automation executions today.
                  </p>
                  <p className="text-xs text-gray-400 mb-2">
                    <span className="text-blue-400">Example:</span> If a campaign's daily budget decreased from R50 to R25, that's R25 saved per day.
                  </p>
                  <p className="text-xs text-gray-400 mb-2">
                    <span className="text-amber-400">Note:</span> Budget increases show as negative savings (money spent).
                  </p>
                  <p className="text-xs text-gray-500 italic">
                    Tip: View History page for complete before/after details
                  </p>
                </div>
              </div>
            </div>
            <h3 className="text-gray-400 text-sm font-medium group-hover:text-blue-300 transition-colors">
              Budget Saved (Today)
            </h3>
            <p className="text-3xl font-bold text-white mt-2">
              R{stats.budgetSaved.toFixed(2)}
            </p>
            <div className="flex items-center gap-1 text-xs text-blue-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <span>View history</span>
              <ArrowRight className="w-3 h-3" />
            </div>
          </button>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              Quick Actions
            </h3>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/campaigns')}
                className="w-full text-left px-4 py-3 bg-cyan-900/30 border border-cyan-700 hover:bg-cyan-900/50 hover:border-cyan-500 rounded-lg transition-all"
              >
                <div className="font-medium text-cyan-100">
                  View Campaigns
                </div>
                <div className="text-sm text-cyan-300">
                  Manage your Google Ads campaigns
                </div>
              </button>

              <button
                onClick={() => navigate('/rules')}
                className="w-full text-left px-4 py-3 bg-emerald-900/30 border border-emerald-700 hover:bg-emerald-900/50 hover:border-emerald-500 rounded-lg transition-all"
              >
                <div className="font-medium text-emerald-100">
                  Create Weather Rule
                </div>
                <div className="text-sm text-emerald-300">
                  Set up automated bid adjustments
                </div>
              </button>

              <button
                onClick={() => navigate('/history')}
                className="w-full text-left px-4 py-3 bg-slate-900/30 border border-slate-700 hover:bg-slate-900/50 hover:border-slate-500 rounded-lg transition-all"
              >
                <div className="font-medium text-slate-100">
                  View History
                </div>
                <div className="text-sm text-slate-300">
                  See all bid adjustment history
                </div>
              </button>

              {/* Dry Run Mode Indicator */}
              <div className={`px-4 py-3 border-2 rounded-lg ${
                dryRunMode
                  ? 'bg-green-900/30 border-green-500/50'
                  : 'bg-red-900/30 border-red-500/50'
              }`}>
                <div className="flex items-start gap-3 mb-2">
                  <Shield className={`w-6 h-6 flex-shrink-0 ${
                    dryRunMode ? 'text-green-400' : 'text-red-400'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-semibold text-white">
                        {dryRunMode ? '✅ Dry Run Mode Active' : '⚠️ Live Mode Active'}
                      </div>
                      <button
                        onClick={() => setShowLiveModeHelp(true)}
                        className="text-xs text-cyan-400 hover:text-cyan-300 underline"
                      >
                        Learn more
                      </button>
                    </div>
                    <div className={`text-sm mb-2 ${
                      dryRunMode ? 'text-green-300' : 'text-red-300'
                    }`}>
                      {dryRunMode
                        ? 'System will simulate changes without affecting real bids'
                        : 'System will make REAL bid adjustments to Google Ads'
                      }
                    </div>
                  </div>
                </div>

                {/* Automation Info */}
                <div className="space-y-2 mb-3 pl-9">
                  <div className="flex items-center gap-2 text-xs text-amber-300">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <span><strong>⚠️ CRITICAL READ - Cooldown:</strong> 2 hours minimum between adjustments</span>
                  </div>
                  <div className="flex items-start gap-2 text-xs text-gray-300">
                    <Info className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <span>
                      {dryRunMode
                        ? 'Changes are logged in History but not sent to Google Ads. Perfect for testing rules.'
                        : 'All bid adjustments are immediately applied to your campaigns. Monitor carefully!'
                      }
                    </span>
                  </div>
                </div>

                {/* Change Settings Button */}
                <button
                  onClick={() => navigate('/settings')}
                  className={`w-full text-center py-2 rounded border transition-all text-sm font-medium ${
                    dryRunMode
                      ? 'bg-green-800/30 border-green-600 hover:bg-green-800/50 text-green-100'
                      : 'bg-red-800/30 border-red-600 hover:bg-red-800/50 text-red-100'
                  }`}
                >
                  Change in Settings →
                </button>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-6">
            {/* City Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Select City
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-cyan-400 pointer-events-none" />
                <select
                  value={selectedCity}
                  onChange={(e) => handleCityChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white font-medium focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 transition-all appearance-none cursor-pointer hover:border-gray-600"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2367e8f9'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '1.5rem',
                  }}
                >
                  {CITIES.map((city) => (
                    <option key={city.name} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Cloud className="h-5 w-5 text-cyan-400" />
                Current Weather Comparison
              </h3>
              <button
                onClick={() => setShowWeatherSourceHelp(true)}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded-lg text-cyan-400 hover:text-cyan-300 transition-all animate-pulse hover:animate-none"
                title="Learn about dual weather sources"
              >
                <HelpCircle className="w-5 h-5" />
                <span className="text-xs font-medium">Why two sources?</span>
              </button>
            </div>
            <p className="text-xs text-gray-400 mb-4">Dual-source verification for better decision-making</p>

            <div className="space-y-4">
              {/* Temperature Comparison */}
              <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
                <div className="text-sm text-gray-400 mb-2 flex items-center justify-between">
                  <span>Temperature</span>
                  <span className="text-xs text-cyan-400">
                    Avg: {weather.loading ? '...' : `${Math.round((weather.openMeteo.temperature + weather.weatherApi.temperature) / 2)}°C`}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-2 bg-gray-900/50 rounded">
                    <div className="text-xs text-gray-500">Open-Meteo</div>
                    <div className="text-lg font-bold text-white">
                      {weather.loading ? '...' : `${weather.openMeteo.temperature}°C`}
                    </div>
                  </div>
                  <div className="text-center p-2 bg-gray-900/50 rounded">
                    <div className="text-xs text-gray-500">WeatherAPI</div>
                    <div className="text-lg font-bold text-white">
                      {weather.loading ? '...' : `${weather.weatherApi.temperature}°C`}
                    </div>
                  </div>
                </div>
              </div>

              {/* Rain Probability Comparison */}
              <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
                <div className="text-sm text-gray-400 mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Droplets className="h-4 w-4 text-blue-400" />
                    Rain Probability
                  </span>
                  <span className="text-xs text-cyan-400">
                    Avg: {weather.loading ? '...' : `${Math.round((weather.openMeteo.rainProbability + weather.weatherApi.rainProbability) / 2)}%`}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-2">
                  <div className="text-center p-2 bg-gray-900/50 rounded">
                    <div className="text-xs text-gray-500">Open-Meteo</div>
                    <div className="text-xl font-bold text-white">
                      {weather.loading ? '...' : `${weather.openMeteo.rainProbability}%`}
                    </div>
                  </div>
                  <div className="text-center p-2 bg-gray-900/50 rounded">
                    <div className="text-xs text-gray-500">WeatherAPI</div>
                    <div className="text-xl font-bold text-white">
                      {weather.loading ? '...' : `${weather.weatherApi.rainProbability}%`}
                    </div>
                  </div>
                </div>
                {!weather.loading && Math.abs(weather.openMeteo.rainProbability - weather.weatherApi.rainProbability) > 20 && (
                  <div className="text-xs text-cyan-300 mt-2 p-2 bg-cyan-900/20 rounded border border-cyan-500/30">
                    ℹ️ <strong>Variance: {Math.abs(weather.openMeteo.rainProbability - weather.weatherApi.rainProbability)}%</strong> - Sources differ, consider reviewing both
                  </div>
                )}
              </div>

              {/* Humidity Comparison */}
              <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
                <div className="text-sm text-gray-400 mb-2 flex items-center justify-between">
                  <span>Humidity</span>
                  <span className="text-xs text-cyan-400">
                    Avg: {weather.loading ? '...' : `${Math.round((weather.openMeteo.humidity + weather.weatherApi.humidity) / 2)}%`}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-2 bg-gray-900/50 rounded">
                    <div className="text-xs text-gray-500">Open-Meteo</div>
                    <div className="text-lg font-bold text-white">
                      {weather.loading ? '...' : `${weather.openMeteo.humidity}%`}
                    </div>
                  </div>
                  <div className="text-center p-2 bg-gray-900/50 rounded">
                    <div className="text-xs text-gray-500">WeatherAPI</div>
                    <div className="text-lg font-bold text-white">
                      {weather.loading ? '...' : `${weather.weatherApi.humidity}%`}
                    </div>
                  </div>
                </div>
              </div>

              {/* Wind Speed Comparison */}
              <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
                <div className="text-sm text-gray-400 mb-2 flex items-center justify-between">
                  <span>Wind Speed</span>
                  <span className="text-xs text-cyan-400">
                    Avg: {weather.loading ? '...' : `${Math.round((weather.openMeteo.windSpeed + weather.weatherApi.windSpeed) / 2)} km/h`}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-2 bg-gray-900/50 rounded">
                    <div className="text-xs text-gray-500">Open-Meteo</div>
                    <div className="text-lg font-bold text-white">
                      {weather.loading ? '...' : `${weather.openMeteo.windSpeed} km/h`}
                    </div>
                  </div>
                  <div className="text-center p-2 bg-gray-900/50 rounded">
                    <div className="text-xs text-gray-500">WeatherAPI</div>
                    <div className="text-lg font-bold text-white">
                      {weather.loading ? '...' : `${weather.weatherApi.windSpeed} km/h`}
                    </div>
                  </div>
                </div>
              </div>

              {/* Last Automation Activity */}
              <div className="mt-4 p-3 bg-amber-900/30 border border-amber-700 rounded-lg">
                <div className="flex items-start space-x-2">
                  <Zap className="h-5 w-5 text-amber-400 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-amber-100 text-sm">
                      {loadingExecution ? 'Loading...' : lastExecution ? 'Last Automation' : 'No Recent Activity'}
                    </div>
                    {loadingExecution ? (
                      <div className="text-xs text-amber-300">
                        Checking automation status...
                      </div>
                    ) : lastExecution ? (
                      <div className="text-xs text-amber-300">
                        {lastExecution.success ? (
                          <>
                            {lastExecution.dry_run && '[DRY RUN] '}
                            {lastExecution.bid_adjustment_type === 'increase' ? 'Increased' : 'Decreased'} bids by {Math.abs(parseFloat(lastExecution.bid_adjustment_percent))}%
                            {' • '}
                            {new Date(lastExecution.executed_at).toLocaleString('en-ZA', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              timeZone: 'Africa/Johannesburg' // Force SAST (UTC+2)
                            })}
                          </>
                        ) : (
                          `Failed: ${lastExecution.error_message || 'Unknown error'}`
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-400">
                        No automations have been triggered yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Satellite Weather Images - Dual View */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-lg font-semibold text-white">
              Live Satellite - Southern Africa
            </h3>
            <div className="relative group">
              <Info className="w-4 h-4 text-gray-400 hover:text-cyan-400 cursor-help" />
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 border border-cyan-700 rounded-lg text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                Dual satellite view: Infrared + Precipitation radar
              </div>
            </div>
          </div>

          {/* Dual Satellite Image Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* High Altitude Infrared Satellite */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-cyan-400 flex items-center gap-2">
                  <Cloud className="h-4 w-4" />
                  High Altitude Infrared Satellite
                </h4>
                <span className="text-xs text-gray-500">Koolasun</span>
              </div>
              <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden" style={{ height: '400px' }}>
                <img
                  src={`${window.location.origin}/satellite-cache/infrared-latest.gif?t=${satelliteImageKey}`}
                  alt="Southern Africa - High Altitude Infrared Satellite"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgZmlsbD0iIzFmMjkzNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM5Y2E3YjciIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5TYXRlbGxpdGUgaW1hZ2UgdW5hdmFpbGFibGU8L3RleHQ+PC9zdmc+';
                  }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">IR 0.39 wavelength - Cloud cover & temperature</p>
            </div>

            {/* RGB Air Mass */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-purple-400 flex items-center gap-2">
                  <Cloud className="h-4 w-4" />
                  RGB Air Mass (Last 5 Hours)
                </h4>
                <span className="text-xs text-gray-500">EUMETSAT</span>
              </div>
              <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden" style={{ height: '400px' }}>
                <img
                  src={`${window.location.origin}/satellite-cache/airmass-latest.gif?t=${satelliteImageKey}`}
                  alt="EUMETSAT RGB Air Mass - Southern Africa"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgZmlsbD0iIzFmMjkzNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM5Y2E3YjciIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5BaXIgbWFzcyBkYXRhIHVuYXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==';
                  }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">Animated composite - Air masses & jet streams</p>
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-4 text-center border-t border-gray-700 pt-3">
            📡 Cached locally (updates hourly at :03) • Infrared: Koolasun • Air Mass: EUMETSAT
          </p>
        </div>

        {/* Recent Activity */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg">
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white">
              Recent Activity
            </h3>
          </div>
          <div className="divide-y divide-gray-700">
            {activities.length === 0 ? (
              <div className="p-8 text-center">
                <Activity className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">No Recent Activity</h3>
                <p className="text-gray-400 text-sm mb-6">
                  Activity will appear here when you create, edit, or toggle weather rules.
                </p>
                <div className="space-y-2 text-sm text-left bg-cyan-900/20 border border-cyan-700/30 rounded-lg p-4 max-w-md mx-auto">
                  <p className="text-cyan-300 font-semibold mb-2">👉 Quick Start Steps:</p>
                  <div className="space-y-1 text-gray-300">
                    <p>1. Go to <strong>Campaigns</strong> page → Sync campaigns from Google Ads</p>
                    <p>2. Go to <strong>Rules</strong> page → Create your first weather rule</p>
                    <p>3. Wait for automation to run (every 30 min)</p>
                    <p>4. Check <strong>History</strong> page for executions</p>
                  </div>
                </div>
              </div>
            ) : (
              activities.map((activity) => (
                <div key={activity.id} className="p-6">
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 border rounded-lg ${getActivityColorClasses(activity.color)}`}>
                      {getActivityIcon(activity.icon)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">
                        {activity.action}
                      </p>
                      <p className="text-sm text-gray-400">
                        {activity.description}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatTimeAgo(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Weather Source Help Modal */}
        {showWeatherSourceHelp && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-3xl w-full border border-cyan-500/20">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <h3 className="text-xl font-semibold text-white">Why Two Weather Sources?</h3>
                <button onClick={() => setShowWeatherSourceHelp(false)}>
                  <X className="w-6 h-6 text-gray-400 hover:text-white transition-colors" />
                </button>
              </div>
              {/* Body */}
              <div className="p-6 space-y-4">
                <p className="text-gray-300">
                  We use <strong className="text-cyan-400">TWO independent weather APIs</strong> to ensure accuracy and reliability:
                </p>

                <div className="space-y-3">
                  <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-300 mb-2">🌍 Open-Meteo (European)</h4>
                    <ul className="text-sm text-gray-300 space-y-1 ml-4 list-disc">
                      <li>Free, unlimited API calls</li>
                      <li>10km weather resolution</li>
                      <li>Open-source weather models</li>
                    </ul>
                  </div>

                  <div className="bg-purple-900/30 border border-purple-700 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-300 mb-2">🇬🇧 WeatherAPI.com (UK)</h4>
                    <ul className="text-sm text-gray-300 space-y-1 ml-4 list-disc">
                      <li>1 million calls/month free tier</li>
                      <li>5km weather resolution</li>
                      <li>Commercial-grade accuracy</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-cyan-900/20 border border-cyan-700/30 rounded-lg p-4">
                  <h4 className="font-semibold text-cyan-300 mb-2">📊 How to Read This:</h4>
                  <div className="space-y-2 text-sm text-gray-300">
                    <p>
                      <span className="text-green-400">✅ Values close together</span> (difference &lt; 5%) = <strong>High confidence</strong>
                    </p>
                    <p>
                      <span className="text-amber-400">⚠️ Values differ significantly</span> (difference &gt; 20%) = <strong>Exercise caution</strong>
                    </p>
                    <p className="mt-2 text-gray-400">
                      The <strong className="text-cyan-400">"Avg"</strong> value shown is used by the automation system for decision-making.
                    </p>
                  </div>
                </div>

                <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">💡 Example:</h4>
                  <div className="space-y-1 text-sm text-gray-300">
                    <p>• Rain shows <span className="text-white">60% vs 65%</span> → Normal variance (5% diff)</p>
                    <p>• Rain shows <span className="text-amber-400">20% vs 80%</span> → Large variance (60% diff) - wait 30min and recheck</p>
                  </div>
                </div>
              </div>
              {/* Footer */}
              <div className="border-t border-gray-700 p-6">
                <button
                  onClick={() => setShowWeatherSourceHelp(false)}
                  className="w-full px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Live Mode Help Modal */}
        {showLiveModeHelp && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-3xl w-full border border-cyan-500/20">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <h3 className="text-xl font-semibold text-white">Understanding Live Mode vs Dry Run</h3>
                <button onClick={() => setShowLiveModeHelp(false)}>
                  <X className="w-6 h-6 text-gray-400 hover:text-white transition-colors" />
                </button>
              </div>
              {/* Body */}
              <div className="p-6 space-y-4">
                <div className={`border-2 rounded-lg p-4 ${
                  dryRunMode
                    ? 'bg-green-900/30 border-green-500'
                    : 'bg-red-900/30 border-red-500'
                }`}>
                  <p className="text-white font-semibold mb-2">
                    CURRENTLY: {dryRunMode ? '🟢 Dry Run Mode Active' : '🔴 Live Mode Active'}
                  </p>
                  <p className={`text-sm ${dryRunMode ? 'text-green-300' : 'text-red-300'}`}>
                    All bid/budget changes are {dryRunMode ? 'simulated only (no real Google Ads changes)' : 'applied to Google Ads immediately'}.
                  </p>
                </div>

                <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-3">🔄 How to Switch Safely:</h4>
                  <ol className="text-sm text-gray-300 space-y-2 ml-4 list-decimal">
                    <li>Go to <strong className="text-cyan-400">Settings</strong> page</li>
                    <li>Toggle <strong className="text-cyan-400">"Dry Run Mode"</strong> {dryRunMode ? 'OFF' : 'ON'}</li>
                    <li>Test rules for <strong>7+ days</strong> (if enabling live)</li>
                    <li>Review <strong>execution history</strong> daily</li>
                    <li>Monitor carefully when live</li>
                  </ol>
                </div>

                <div className="space-y-3">
                  <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
                    <h4 className="font-semibold text-green-300 mb-2">🟢 Dry Run Mode:</h4>
                    <ul className="text-sm text-gray-300 space-y-1 ml-4 list-disc">
                      <li>Perfect for <strong>testing new rules</strong></li>
                      <li>No real money spent</li>
                      <li>See what <em>would</em> happen in History page</li>
                      <li>Safe to experiment with thresholds</li>
                    </ul>
                  </div>

                  <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
                    <h4 className="font-semibold text-red-300 mb-2">🔴 Live Mode:</h4>
                    <ul className="text-sm text-gray-300 space-y-1 ml-4 list-disc">
                      <li>Real bid/budget changes to Google Ads</li>
                      <li><strong className="text-red-400">Spends real money</strong></li>
                      <li>Use only after thorough testing</li>
                      <li>Monitor daily for unexpected behavior</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-amber-900/20 border border-amber-700 rounded-lg p-4">
                  <p className="text-amber-300 text-sm">
                    <strong>⚠️ Recommendation:</strong> Always test in Dry Run Mode for at least 7 days before going live. Review all executions in the History page to verify rules are triggering correctly.
                  </p>
                </div>
              </div>
              {/* Footer */}
              <div className="border-t border-gray-700 p-6">
                <button
                  onClick={() => setShowLiveModeHelp(false)}
                  className="w-full px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Development Status Modal - TEMPORARY */}
        {showNextStepsModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-5xl w-full border-2 border-cyan-500/50 max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 p-6 border-b-2 border-cyan-400 z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-white rounded-full p-2">
                      <Info className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">🛠️ Development Status Update</h3>
                      <p className="text-sm text-cyan-100 mt-1">TEMPORARY MODAL - For development purposes only</p>
                    </div>
                  </div>
                  <button onClick={() => setShowNextStepsModal(false)}>
                    <X className="w-6 h-6 text-white hover:text-gray-200 transition-colors" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="p-8 space-y-6">
                {/* NOTE: This modal is temporary for development handover */}
                <div className="bg-amber-900/30 border-2 border-amber-500 rounded-xl p-4">
                  <p className="text-amber-300 text-sm">
                    <strong>📝 Note:</strong> This modal is temporary for development handover purposes. Remove this component before final production deployment.
                  </p>
                </div>

                {/* System Status Update */}
                <div className="bg-green-900/30 border-2 border-green-500 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-green-300 mb-4 flex items-center gap-2">
                    ✅ ALL BUGS FIXED & DEPLOYED (Jan 12, 2025)
                  </h4>
                  <div className="space-y-3 text-gray-200">
                    <div className="bg-black/30 rounded-lg p-4 border-l-4 border-green-500">
                      <p className="font-bold text-white mb-2">Bug #1: OAuth Token Overwrite - FIXED ✅</p>
                      <p className="text-sm text-gray-300">
                        <strong className="text-red-400">Problem:</strong> Discover Accounts button was overwriting OAuth tokens for all accounts with the same token, breaking permissions.
                      </p>
                      <p className="text-sm text-gray-300 mt-1">
                        <strong className="text-green-400">Fixed:</strong> Now preserves existing OAuth tokens per account.
                      </p>
                    </div>

                    <div className="bg-black/30 rounded-lg p-4 border-l-4 border-green-500">
                      <p className="font-bold text-white mb-2">Bug #2: Automation Override - FIXED ✅</p>
                      <p className="text-sm text-gray-300">
                        <strong className="text-red-400">Problem:</strong> Sync Campaigns button was force-enabling automation on ALL campaigns, ignoring your preferences.
                      </p>
                      <p className="text-sm text-gray-300 mt-1">
                        <strong className="text-green-400">Fixed:</strong> Now preserves your automation settings. New campaigns default to disabled.
                      </p>
                    </div>

                    <div className="bg-black/30 rounded-lg p-4 border-l-4 border-green-500">
                      <p className="font-bold text-white mb-2">Bug #3: Sync Failure on Bad Accounts - FIXED ✅</p>
                      <p className="text-sm text-gray-300">
                        <strong className="text-red-400">Problem:</strong> If one account failed (e.g., deactivated), entire sync would fail for all accounts.
                      </p>
                      <p className="text-sm text-gray-300 mt-1">
                        <strong className="text-green-400">Fixed:</strong> Now handles errors per account. Good accounts sync even if one is broken.
                      </p>
                    </div>

                    <div className="bg-black/30 rounded-lg p-4 border-l-4 border-green-500">
                      <p className="font-bold text-white mb-2">Bug #4: Peppermills Account Missing MCC Link - FIXED ✅</p>
                      <p className="text-sm text-gray-300">
                        <strong className="text-red-400">Problem:</strong> Peppermills (923-045-9725) was in database but missing mcc_customer_id, making it invisible during campaign sync.
                      </p>
                      <p className="text-sm text-gray-300 mt-1">
                        <strong className="text-green-400">Fixed:</strong> Database updated to link Peppermills to MCC 765-346-1635. All 8 accounts now properly configured.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Current Account Status */}
                <div className="bg-cyan-900/30 border-2 border-cyan-500 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-cyan-300 mb-4 flex items-center gap-2">
                    📊 Google Ads Account Structure & Configuration
                  </h4>
                  <div className="bg-black/50 rounded-lg p-6 space-y-4">
                    <div>
                      <p className="font-bold text-cyan-300 mb-3">MCC (Manager Account) Structure:</p>
                      <ul className="text-sm text-gray-300 space-y-2 ml-4 list-disc">
                        <li>
                          <strong className="text-white">MCC 765-346-1635</strong> (NETWORDS) - Parent manager account
                          <p className="text-xs text-gray-400 mt-1">Provides OAuth access to all child accounts below</p>
                        </li>
                        <li>
                          <strong className="text-amber-400">Nested Sub-MCCs:</strong> Google Ads may have intermediate manager accounts (tiers)
                          <p className="text-xs text-gray-400 mt-1">These are automatically flattened by the API - you don't need to configure them separately</p>
                        </li>
                        <li>
                          <strong className="text-green-400">Client Accounts:</strong> Individual advertiser accounts under the MCC hierarchy
                          <p className="text-xs text-gray-400 mt-1">Each client account can contain multiple campaigns</p>
                        </li>
                      </ul>
                    </div>

                    <div className="border-t border-gray-700 pt-4">
                      <p className="font-bold text-cyan-300 mb-3">Currently Connected Accounts (as of sync):</p>
                      <ul className="ml-6 space-y-1 text-xs">
                        <li>- <strong className="text-white">923-045-9725</strong> (Peppermills For Africa) <span className="text-green-400">✅ FIXED!</span> - 0 campaigns (sync needed)</li>
                        <li>- 421-574-2971 (Drain Surgeon) - 148 campaigns</li>
                        <li>- 629-918-6741 (Palian - Duane) - 23 campaigns</li>
                        <li>- 179-498-7337 (Mooikloof Country Estate) - 25 campaigns</li>
                        <li>- 170-401-0129 (UberSolar) - 25 campaigns</li>
                        <li>- 617-360-9412 (DataFin CS) - 16 campaigns</li>
                        <li>- 476-152-6072 (Deactivated) - 0 campaigns</li>
                        <li>- 765-346-1635 (NETWORDS MCC) - 0 campaigns (manager only)</li>
                      </ul>
                      <p className="text-sm text-cyan-400 mt-3">• <strong>Total:</strong> 8 accounts, 237 campaigns (across 6 active client accounts)</p>
                      <p className="text-xs text-gray-400 mt-2">
                        <strong>Note:</strong> Additional accounts may be added automatically when you click "Discover Accounts" - the system will find ALL accounts accessible under MCC 765-346-1635, regardless of nesting depth.
                      </p>
                    </div>

                    <div className="border-t border-gray-700 pt-4">
                      <p className="font-bold text-amber-300 mb-2">🔐 OAuth Authentication:</p>
                      <p className="text-sm text-gray-300">
                        <strong>Single Token for All:</strong> One set of OAuth credentials (refresh token) for MCC 765-346-1635 grants API access to ALL child accounts, regardless of how many tiers deep they're nested.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Next Steps */}
                <div className="bg-blue-900/30 border-2 border-blue-500 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-blue-300 mb-4 flex items-center gap-2">
                    📋 Next Steps for Alex
                  </h4>
                  <div className="bg-black/50 rounded-lg p-6 space-y-4">
                    <div className="border-l-4 border-green-500 pl-4">
                      <p className="font-bold text-green-300 mb-2">Step 1: Sync Peppermills Campaigns</p>
                      <p className="text-gray-200 text-sm mb-2">
                        Go to <strong className="text-cyan-400">Campaigns</strong> page → Click <strong className="text-white">"Sync from Google Ads"</strong> button
                      </p>
                      <p className="text-xs text-gray-400">
                        Expected result: Peppermills campaign(s) will now appear in the campaigns list.
                      </p>
                    </div>

                    <div className="border-l-4 border-cyan-500 pl-4">
                      <p className="font-bold text-cyan-300 mb-2">Step 2: Create Weather Rules</p>
                      <p className="text-gray-200 text-sm mb-2">
                        Go to <strong className="text-cyan-400">Weather Rules</strong> page → Create automation rules for campaigns
                      </p>
                      <p className="text-xs text-gray-400">
                        Tip: Start with conservative thresholds (e.g., rain ≥80%) and monitor for 7+ days.
                      </p>
                    </div>

                    <div className="border-l-4 border-amber-500 pl-4">
                      <p className="font-bold text-amber-300 mb-2">Step 3: Monitor Dry Run Mode</p>
                      <p className="text-gray-200 text-sm mb-2">
                        Keep <strong className="text-white">Dry Run Mode ENABLED</strong> in Settings for at least 7 days
                      </p>
                      <p className="text-xs text-gray-400">
                        System will log all "would-be" adjustments without making real changes to Google Ads.
                      </p>
                    </div>

                    <div className="border-l-4 border-purple-500 pl-4">
                      <p className="font-bold text-purple-300 mb-2">Step 4: Review History Daily</p>
                      <p className="text-gray-200 text-sm mb-2">
                        Check <strong className="text-cyan-400">History</strong> page daily to verify rules are triggering correctly
                      </p>
                      <p className="text-xs text-gray-400">
                        Look for execution logs showing weather conditions and calculated bid adjustments.
                      </p>
                    </div>
                  </div>
                </div>

                {/* OAuth Structure Explanation */}
                <div className="bg-purple-900/30 border-2 border-purple-500 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-purple-300 mb-4 flex items-center gap-2">
                    🔐 OAuth & MCC Architecture
                  </h4>
                  <div className="bg-black/50 rounded-lg p-4 space-y-3 text-sm text-gray-300">
                    <p className="font-bold text-white">How OAuth Works with MCC Structure:</p>
                    <ul className="ml-4 space-y-2 list-disc">
                      <li><strong className="text-cyan-400">Single OAuth Token:</strong> One set of OAuth credentials (refresh token) for the main MCC account (765-346-1635)</li>
                      <li><strong className="text-green-400">Shared Access:</strong> That single token grants API access to ALL 8 child accounts under the MCC</li>
                      <li><strong className="text-amber-400">Nested Structure:</strong> Google Ads has sub-MCCs (intermediate tiers), but the API automatically flattens the hierarchy</li>
                      <li><strong className="text-purple-400">login_customer_id:</strong> API uses MCC ID as "login" credential, then targets specific child accounts</li>
                    </ul>
                    <p className="text-xs text-gray-400 mt-3 pt-3 border-t border-gray-700">
                      ℹ️ This is why all 8 accounts show the same mcc_customer_id (765-346-1635) in the database - they all authenticate through the parent MCC.
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-t-2 border-amber-500 p-6 flex gap-3">
                <button
                  onClick={() => {
                    setShowNextStepsModal(false);
                    navigate('/campaigns');
                  }}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowRight className="w-5 h-5" />
                  Go to Campaigns Page
                </button>
                <button
                  onClick={() => {
                    setShowNextStepsModal(false);
                    navigate('/weather-rules');
                  }}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowRight className="w-5 h-5" />
                  Go to Weather Rules
                </button>
                <button
                  onClick={() => setShowNextStepsModal(false)}
                  className="px-6 py-3 bg-gradient-to-r from-gray-700 to-gray-600 hover:from-gray-600 hover:to-gray-500 text-white font-medium rounded-lg transition-colors"
                >
                  I'll Answer Later
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
